// Libralies
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Text,
    Flex,
    useMediaQuery,
    Skeleton,
    SimpleGrid,
    VStack,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    useDisclosure
} from '@chakra-ui/react'

// Core
import { useGetContactsQuery } from '@core/app/api/contactsApi'
import { Layout } from '@core/components/Layout/Layout'

// Hooks
import useProcessedData from './hooks/useProcessedData'

// Components
import AsideDropdown from './components/AsideDropdown/AsideDropdown'
import DepartmentDetails from './components/DepartmentDetails/DepartmentDetails'
import UserList from './components/UserList/UserList'
import ContactForm from './components/ContactForm/ContactForm'
import EmptyState from './components/EmptyState/EmptyState'

// Types
import { Department } from '@core/app/types/contactsApiType'

export const ContactPage = () => {
    const { t } = useTranslation()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { data: contacts, isLoading } = useGetContactsQuery()
    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null)

    const processedData = useProcessedData(contacts?.data || [])
    const countriesArray = Object.values(processedData || {})

    const [isDesktop] = useMediaQuery('(min-width: 1230px)', {
        fallback: false,
    })

    useEffect(() => {
        // Ensure there is at least one department in the first country
        const firstDepartment = countriesArray[0]?.departments
            ? Object.values(countriesArray[0].departments || {})[0] as Department
            : null

        // If no department is selected and firstDepartment exists, set it
        if (!selectedDepartment && firstDepartment) {
            setSelectedDepartment(firstDepartment)
        }
    }, [countriesArray, selectedDepartment])

    const handleDepartmentSelect = (department: Department | null) => {
        setSelectedDepartment(department)
    }

    return (
        <Layout useMobileHeader={true} bgColor={'#EDEEF0'}>
            <Box as={'section'}  py={{ base: 6, xl: 8 }} h={'full'}>
                {!isLoading && countriesArray.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <Flex direction={isDesktop ? "row" : "column"} gap={isDesktop ? '100px' : '6'}>
                            {/* Aside with departments */}
                            <Flex as={'aside'} minWidth={isDesktop ? '196px' : '100%'} direction={"column"} flexShrink={0}>
                                {isLoading ? (
                                    <>
                                        <Skeleton w={'50%'} h={'30px'} mb={8}/>
                                        <Skeleton w={'full'} h={'150px'}/>
                                    </>
                                ) : (
                                    <>
                                        <Text color="#1A1A1A" fontWeight="600" fontSize="xl" mb={6}>{t('7767')}</Text>
                                        <AsideDropdown
                                            selectedOnly={!isDesktop}
                                            countries={countriesArray}
                                            selectedDepartment={selectedDepartment}
                                            onSelectDepartment={handleDepartmentSelect}
                                            onAccordionButtonClick={!isDesktop ? onOpen : undefined}
                                        />
                                    </>
                                )}
                            </Flex>

                            {/* Page content */}
                            <Box w={'full'} mb={8}>
                                <SimpleGrid
                                    as='section'
                                    columns={{ base: 1, xl: 2 }}
                                    mb={isDesktop ? '72px' : '40px'}
                                    spacing={5}
                                >
                                    <VStack align="start" maxW={"384px"} w={"100%"}>
                                        {isLoading ? (
                                            <>
                                                <Skeleton w="50%" height="60px" mb={isDesktop ? 8 : 6} />
                                                <Skeleton w='80%' height="80px" mb={isDesktop ? 8 : 6} />
                                            </>
                                        ) : selectedDepartment && (
                                            <DepartmentDetails selectedDepartment={selectedDepartment} />
                                        )}
                                    </VStack>
                                    {isDesktop && <ContactForm selectedDepartment={selectedDepartment} isLoading={isLoading} />}
                                </SimpleGrid>
                                <SimpleGrid as={'section'}>
                                    {isLoading ? (
                                        <Box mb={!isDesktop ? '40px' : 0}>
                                            <Skeleton w={'185px'} h={'42px'} mb={6} />
                                            <SimpleGrid columns={[2, 3]} spacing={isDesktop ? 10 : 4}>
                                                {[...Array(3)].map((_, index) => (
                                                    <VStack align="start" maxW={"280px"} height="280px" w={"100%"} mt={4} key={index}>
                                                        <Skeleton key={index} w={"100%"} height="280px" borderRadius={8} mb={4} />
                                                        <Skeleton maxWidth="180px" height="24px" w={"100%"} mb={2} />
                                                        <Skeleton maxWidth="150px" height="21px" w={"100%"} />
                                                    </VStack>
                                                ))}
                                            </SimpleGrid>
                                        </Box>
                                    ) : (
                                        selectedDepartment && (selectedDepartment.users || []).length ? (
                                            <Box mb={!isDesktop ? '40px' : 0}>
                                                <Text color="#1A1A1A" fontWeight="600" fontSize="28px" mb={6}>{t('217')}</Text>
                                                <UserList users={selectedDepartment.users} />
                                            </Box>
                                        ) : null
                                    )}
                                </SimpleGrid>
                                {!isDesktop && <ContactForm selectedDepartment={selectedDepartment} isLoading={isLoading} />}
                            </Box>
                        </Flex>

                        {!isDesktop && (
                            <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
                                <DrawerOverlay />
                                <DrawerContent>
                                    <Box borderBottomWidth="1px" borderColor={'border.1'} px="24px" py="16px" mb={3}>
                                        <Text color="#1A1A1A" fontWeight="600" fontSize="xl">{t('7767')}</Text>
                                    </Box>
                                    <DrawerBody>
                                        <AsideDropdown
                                            countries={countriesArray}
                                            selectedDepartment={selectedDepartment}
                                            onSelectDepartment={handleDepartmentSelect}
                                            onAccordionPanelClick={onClose}
                                        />
                                    </DrawerBody>
                                </DrawerContent>
                            </Drawer>
                        )}
                    </>
                )}
            </Box>
        </Layout>
    )
}
